import { useEffect, useRef, useState } from "react";
import { Play, Pause, Volume2, VolumeX, Maximize } from "lucide-react";

interface DemoVideoPlayerProps {
  src: string;
}

const formatTime = (s: number) => {
  if (!isFinite(s) || s < 0) s = 0;
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
};

export const DemoVideoPlayer = ({ src }: DemoVideoPlayerProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const seekRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);
  const activePointerRef = useRef<number | null>(null);
  const durationRef = useRef(0);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showControls, setShowControls] = useState(true);
  const hideTimer = useRef<number | null>(null);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onTime = () => { if (!draggingRef.current) setCurrent(v.currentTime); };
    const onMeta = () => {
      if (isFinite(v.duration) && v.duration > 0) {
        durationRef.current = v.duration;
        setDuration(v.duration);
      }
    };
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    v.addEventListener("timeupdate", onTime);
    v.addEventListener("loadedmetadata", onMeta);
    v.addEventListener("durationchange", onMeta);
    v.addEventListener("play", onPlay);
    v.addEventListener("pause", onPause);
    v.play().catch(() => setPlaying(false));
    return () => {
      v.removeEventListener("timeupdate", onTime);
      v.removeEventListener("loadedmetadata", onMeta);
      v.removeEventListener("durationchange", onMeta);
      v.removeEventListener("play", onPlay);
      v.removeEventListener("pause", onPause);
    };
  }, []);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play().catch(() => {});
    else v.pause();
  };

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  const seekToClientX = (clientX: number) => {
    const el = seekRef.current;
    const v = videoRef.current;
    const dur = durationRef.current;
    if (!el || !v || !dur || !isFinite(dur)) return;
    const rect = el.getBoundingClientRect();
    if (rect.width <= 0) return;
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const t = ratio * dur;
    if (!isFinite(t)) return;
    setCurrent(t);
    v.currentTime = t;
  };

  const onSeekPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    draggingRef.current = true;
    activePointerRef.current = e.pointerId;
    const target = e.currentTarget as HTMLDivElement;
    try { target.setPointerCapture(e.pointerId); } catch {}
    seekToClientX(e.clientX);
  };

  const onSeekPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current || activePointerRef.current !== e.pointerId) return;
    e.preventDefault();
    e.stopPropagation();
    seekToClientX(e.clientX);
  };

  const finishSeek = (e: React.PointerEvent<HTMLDivElement>) => {
    if (activePointerRef.current !== e.pointerId) return;
    e.preventDefault();
    e.stopPropagation();
    seekToClientX(e.clientX);
    draggingRef.current = false;
    activePointerRef.current = null;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch {}
  };

  const requestFullscreen = () => {
    const el = containerRef.current;
    if (!el) return;
    if (document.fullscreenElement) document.exitFullscreen();
    else el.requestFullscreen?.();
  };

  const showAndScheduleHide = () => {
    setShowControls(true);
    if (hideTimer.current) window.clearTimeout(hideTimer.current);
    hideTimer.current = window.setTimeout(() => {
      if (videoRef.current && !videoRef.current.paused) setShowControls(false);
    }, 2500);
  };

  const progress = duration > 0 ? (current / duration) * 100 : 0;

  return (
    <div
      ref={containerRef}
      className="relative w-full h-full group"
      onMouseMove={showAndScheduleHide}
      onMouseLeave={() => playing && setShowControls(false)}
    >
      <video
        ref={videoRef}
        src={src}
        autoPlay
        playsInline
        muted
        preload="auto"
        onClick={togglePlay}
        className="absolute inset-0 h-full w-full object-contain bg-black cursor-pointer"
      />

      {/* Center play overlay when paused */}
      {!playing && (
        <button
          type="button"
          onClick={togglePlay}
          className="absolute inset-0 flex items-center justify-center bg-black/30 transition-opacity"
          aria-label="Play"
        >
          <span className="flex h-20 w-20 items-center justify-center rounded-full bg-primary/90 text-primary-foreground shadow-2xl shadow-primary/50 ring-1 ring-white/20 hover:scale-105 transition-transform">
            <Play className="h-9 w-9 ml-1" fill="currentColor" />
          </span>
        </button>
      )}

      {/* Bottom controls bar */}
      <div
        className={`absolute inset-x-0 bottom-0 z-10 transition-opacity duration-200 ${
          showControls || !playing ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      >
        <div className="bg-gradient-to-t from-black/90 via-black/60 to-transparent pt-10 pb-3 px-4">
          {/* Seek bar */}
          <div
            ref={seekRef}
            role="slider"
            aria-label="Seek"
            aria-valuemin={0}
            aria-valuemax={duration || 0}
            aria-valuenow={current}
            tabIndex={0}
            onPointerDown={onSeekPointerDown}
            onPointerMove={onSeekPointerMove}
            onPointerUp={finishSeek}
            onPointerCancel={finishSeek}
            className="relative h-5 flex items-center cursor-pointer touch-none select-none"
          >
            <div className="absolute inset-x-0 h-1 rounded-full bg-white/20" />
            <div
              className="absolute h-1 rounded-full bg-primary"
              style={{ width: `${progress}%` }}
            />
            <div
              className="absolute h-3.5 w-3.5 rounded-full bg-primary ring-2 ring-white shadow-md -translate-x-1/2"
              style={{ left: `${progress}%` }}
            />
          </div>

          <div className="mt-2 flex items-center gap-3 text-white">
            <button
              type="button"
              onClick={togglePlay}
              className="hover:text-primary-light transition-colors"
              aria-label={playing ? "Pause" : "Play"}
            >
              {playing ? <Pause className="h-5 w-5" fill="currentColor" /> : <Play className="h-5 w-5" fill="currentColor" />}
            </button>
            <button
              type="button"
              onClick={toggleMute}
              className="hover:text-primary-light transition-colors"
              aria-label={muted ? "Unmute" : "Mute"}
            >
              {muted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
            </button>
            <div className="text-xs tabular-nums text-white/80">
              {formatTime(current)} <span className="text-white/40">/</span> {formatTime(duration)}
            </div>
            <div className="ml-auto">
              <button
                type="button"
                onClick={requestFullscreen}
                className="hover:text-primary-light transition-colors"
                aria-label="Fullscreen"
              >
                <Maximize className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
